import { useEffect, useRef, useState } from 'react';
import baseapi, { API_BASE } from '../../baseapi';

type Pt = [number, number];

interface ZoneEditorProps {
  cameraId: string;
  /** Outline already saved for this camera (fractions of the frame), if any. */
  initial: Pt[];
  onSaved: (points: Pt[]) => void;
  onClose: () => void;
}

/**
 * Draw the counting area of a camera on a sharp picture from it.
 *
 * A holding-box camera also sees the boxes next to it and the walkway. With an outline, "people in the
 * box" only counts the people standing inside it (placed by their feet). Click to add corners, drag a
 * corner to move it, click a corner twice to remove it. Saved with PUT /api/cameras/{id}/zone and applied
 * by the engine at once.
 */
export function ZoneEditor({ cameraId, initial, onSaved, onClose }: ZoneEditorProps) {
  const [pts, setPts] = useState<Pt[]>(initial);
  const [ratio, setRatio] = useState(16 / 9);
  const [stamp, setStamp] = useState(() => Date.now());
  const [sharpOk, setSharpOk] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const drag = useRef<number | null>(null);
  const moved = useRef(false);

  useEffect(() => { setPts(initial); }, [cameraId]);   // eslint-disable-line react-hooks/exhaustive-deps

  const at = (e: { clientX: number; clientY: number }): Pt => {
    const r = boxRef.current!.getBoundingClientRect();
    const c = (v: number) => Math.min(1, Math.max(0, v));
    return [c((e.clientX - r.left) / r.width), c((e.clientY - r.top) / r.height)];
  };

  const onDown = (e: React.PointerEvent, i: number) => {
    e.stopPropagation();
    drag.current = i; moved.current = false;
    (e.target as Element).setPointerCapture(e.pointerId);
  };
  const onMove = (e: React.PointerEvent) => {
    if (drag.current === null) return;
    moved.current = true;
    const p = at(e);
    setPts(ps => ps.map((q, i) => (i === drag.current ? p : q)));
  };
  const onUp = () => { drag.current = null; };

  const save = async (points: Pt[]) => {
    setBusy(true); setMsg(null);
    try {
      await baseapi.put(`cameras/${encodeURIComponent(cameraId)}/zone`, { points });
      onSaved(points);
      setMsg(points.length ? 'Saved. People are now counted inside this area only.' : 'Area removed. Everyone in the picture is counted again.');
    } catch {
      setMsg('Not saved. Check the connection and try again.');
    } finally {
      setBusy(false);
    }
  };

  // the camera's saved picture (13 Oct 2026: no live pictures in the portal; still.jpg?w=1920 logged in to the
  // camera for every full-resolution frame)
  const src = `${API_BASE}cameras/${encodeURIComponent(cameraId)}/last-snapshot.jpg?t=${stamp}`;
  const btn = 'px-2.5 py-1 rounded-md text-[11px] font-semibold cursor-pointer disabled:opacity-50';

  return (
    <div>
      <div ref={boxRef} className="relative w-full bg-black select-none touch-none" style={{ aspectRatio: String(ratio) }}
        onPointerMove={onMove} onPointerUp={onUp}
        onClick={e => { if (drag.current === null && !moved.current) setPts(ps => [...ps, at(e)]); moved.current = false; }}>
        <img src={src} alt={cameraId} draggable={false} className="absolute inset-0 w-full h-full"
          onLoad={e => { const i = e.currentTarget; if (i.naturalWidth && i.naturalHeight) setRatio(i.naturalWidth / i.naturalHeight); }}
          onError={() => { if (sharpOk) setSharpOk(false); }} />
        <svg viewBox="0 0 1000 1000" preserveAspectRatio="none" className="absolute inset-0 w-full h-full cursor-crosshair">
          {pts.length >= 2 && (
            <polygon points={pts.map(p => `${p[0] * 1000},${p[1] * 1000}`).join(' ')}
              fill={pts.length >= 3 ? 'rgba(0,200,255,0.18)' : 'none'} stroke="#00c8ff" strokeWidth="3" vectorEffect="non-scaling-stroke" />
          )}
        </svg>
        {pts.map((p, i) => (
          <button key={i} type="button" aria-label={`Corner ${i + 1}`}
            onPointerDown={e => onDown(e, i)}
            onClick={e => e.stopPropagation()}
            onDoubleClick={e => { e.stopPropagation(); setPts(ps => ps.filter((_, j) => j !== i)); }}
            className="absolute w-4 h-4 -ml-2 -mt-2 rounded-full bg-white border-2 border-cyan-500 shadow cursor-grab active:cursor-grabbing"
            style={{ left: `${p[0] * 100}%`, top: `${p[1] * 100}%` }} />
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-1.5 px-3 py-2 border-t border-[#262b37] text-neutral-200">
        <span className="text-[11px] text-neutral-400 mr-auto">
          Click to add corners around the box floor · drag a corner to move it · double-click a corner to remove it
        </span>
        <button className={`${btn} bg-white/10 hover:bg-white/20`} disabled={busy || !pts.length} onClick={() => setPts(ps => ps.slice(0, -1))}>Undo</button>
        <button className={`${btn} bg-white/10 hover:bg-white/20`} disabled={busy || !pts.length} onClick={() => setPts([])}>Clear</button>
        <button className={`${btn} bg-white/10 hover:bg-white/20`} disabled={busy} onClick={() => { setSharpOk(true); setStamp(Date.now()); }}>New picture</button>
        <button className={`${btn} bg-rose-600/80 hover:bg-rose-600 text-white`} disabled={busy || !initial.length} onClick={() => { setPts([]); save([]); }}>Remove area</button>
        <button className={`${btn} bg-emerald-600 hover:bg-emerald-500 text-white`} disabled={busy || pts.length < 3} onClick={() => save(pts)}>
          {busy ? 'Saving…' : 'Save area'}
        </button>
        <button className={`${btn} bg-white/10 hover:bg-white/20`} onClick={onClose}>Done</button>
      </div>
      {msg && <div className="px-3 pb-2 text-[11.5px] text-emerald-300">{msg}</div>}
      {pts.length > 0 && pts.length < 3 && <div className="px-3 pb-2 text-[11.5px] text-amber-300">An area needs at least 3 corners.</div>}
    </div>
  );
}
